import React from "react";
import toast from "react-hot-toast";
import {
  EyeOff, Briefcase, UserCog, FileText, Eye, Shield, Plus,
  Activity as ActivityPerm, UserPlus, Mail,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { useBinding } from "../../hooks/useBinding";
import { Drawer } from "../../components/Drawer";
import { Button } from "../../components/primitives";
import { Field, Input, PasswordInput } from "../../components/forms";
import { ConnectRow, DrawerSection } from "../../components/ConnectRow";
import { Dropdown } from "../../components/FilterBar";
import { Toggle } from "../../components/Toggle";
import { AntDropdown, AntMultiSelect } from "../../../../components/UI";
import { isCrmEnabledForOrg } from "../../../../modules/crm/utils/orgGating";
import { getAccessiblePositions, getManagerId, normalizeReportingManagerIds } from "./teamHelpers";

// Add / edit member drawer. Split out of TeamV2 so the screen file holds data and
// state while this owns the form.
export function MemberFormDrawer({
  open, onClose, onSave, saving, editing, form, setForm, errors, setErrors,
  roles, allUsers, structure, currentUser, getAvailableReportingManagers, onAddPosition,
}) {
  const t = useDashTheme();
  const isNew = editing === "new";
  return (
    <Drawer
      open={open} onClose={onClose}
      title={isNew ? "Add member" : "Edit member"}
      subtitle={isNew ? "Invite someone to your team" : `${editing?.firstName || ""} ${editing?.lastName || ""}`.trim()}
      icon={<UserPlus size={18} />} width={560}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : isNew ? "Add member" : "Save changes"}
        </Button>
      </>}
    >
      <MemberForm
        t={t} form={form} setForm={setForm} errors={errors} setErrors={setErrors}
        roles={roles} managers={allUsers} structure={structure} currentUser={currentUser}
        isNew={isNew} editingUser={!isNew ? editing : null}
        onAddPosition={onAddPosition}
        getAvailableReportingManagers={getAvailableReportingManagers}
      />
    </Drawer>
  );
}

function MemberForm({ t, form, setForm, errors = {}, setErrors, roles, managers, structure, currentUser, isNew, editingUser, onAddPosition, getAvailableReportingManagers }) {
  // Update a field AND clear its inline error live (like the legacy form did),
  // so a filled field never keeps showing a stale "required" message.
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (setErrors) setErrors((e) => (e && e[k] ? { ...e, [k]: undefined } : e));
  };
  const setPerm = (k) => set("permissions", { ...form.permissions, [k]: !form.permissions?.[k] });
  const isMember = form.role !== "org_admin";
  const showCrm = isCrmEnabledForOrg(window.__orgProfile)
    && currentUser?.role !== "org_user";

  // Position options from team structure (hierarchy-filtered, same as legacy).
  const positionOptions = React.useMemo(() => {
    const opts = getAccessiblePositions(structure, currentUser);
    // Ensure the currently-set position stays selectable even if outside accessible set (edit case).
    if (form.position && !opts.some((o) => o.value === form.position)) opts.unshift({ value: form.position, label: form.position });
    return opts;
  }, [structure, currentUser, form.position]);

  // Reporting-manager options — hierarchy-filtered by the target position (same
  // rule as legacy getAvailableReportingManagers), with any already-selected
  // managers kept selectable even if outside the eligible set (edit case).
  const managerOptions = React.useMemo(() => {
    const currentUserId = editingUser?._id || editingUser?.id || null;
    let eligible = [];
    if (typeof getAvailableReportingManagers === "function") {
      eligible = getAvailableReportingManagers(form.position, currentUserId) || [];
    } else {
      eligible = (managers || []).filter((u) => (u._id || u.id) !== currentUserId);
    }
    const byId = new Map(eligible.map((u) => [String(u._id || u.id), u]));
    // Keep already-selected managers selectable even if not in the eligible set.
    (form.reportingManagers || []).forEach((entry) => {
      const sid = String(getManagerId(entry) || entry);
      if (!sid || sid === "undefined") return;
      if (!byId.has(sid)) {
        const found = (managers || []).find((u) => String(u._id || u.id) === sid);
        if (found) byId.set(sid, found);
      }
    });
    return Array.from(byId.values()).map((u) => ({
      value: u._id || u.id,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
    }));
  }, [managers, editingUser, getAvailableReportingManagers, form.position, form.reportingManagers]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
      <DrawerSection title="Profile">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[4] }}>
          <Field label="First name" required error={errors.firstName}><Input invalid={!!errors.firstName} value={form.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="Jane" /></Field>
          <Field label="Last name"><Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Doe" /></Field>
        </div>
        <Field label="Email" required error={errors.email}><Input invalid={!!errors.email} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" /></Field>
        <Field label="Stub email (optional)" hint="Legacy CRM alias shown on the member card; login still uses the email above." error={errors.stubEmail}>
          <Input invalid={!!errors.stubEmail} type="email" value={form.stubEmail} onChange={(e) => set("stubEmail", e.target.value)} placeholder="alias@company.com" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[4] }}>
          <Field label="Role"><AntDropdown value={form.role} options={roles.map((r) => ({ value: r.value, label: r.label }))} onChange={(v) => set("role", v)} allowClear={false} getPopupContainer={(node) => node.parentNode} /></Field>
          <Field label="Position" required={isMember} error={errors.position}>
            {positionOptions.length === 0 ? (
              // No positions defined yet → CTA to set up Team Structure (same as V1).
              <button
                type="button"
                onClick={onAddPosition}
                style={{
                  width: "100%", minHeight: 40, padding: "9px 12px", borderRadius: t.r.sm,
                  border: `1.5px dashed ${t.c.border}`, background: t.c.surfaceMuted, color: t.c.primary,
                  fontSize: t.font.size.md, fontFamily: t.font.family, fontWeight: 500, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  transition: "border-color 120ms, background 120ms",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.c.primary; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.c.border; }}
              >
                <Plus size={16} /> Add position
              </button>
            ) : (
              <AntDropdown
                value={form.position || undefined}
                options={positionOptions}
                onChange={(v) => {
                  setForm((f) => ({ ...f, position: v || "", reportingManagers: [] }));
                  if (setErrors) setErrors((e) => ({ ...e, position: undefined, reportingManagers: undefined }));
                }}
                placeholder="Select a position"
                allowClear
                showSearch
                style={{ width: "100%" }}
                getPopupContainer={(node) => node.parentNode}
              />
            )}
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[4] }}>
          <Field label="Department"><Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Product" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1…" /></Field>
        </div>
        <Field
          label="Approx. compulsion"
          hint="Optional — leave empty if you do not need it."
        >
          <Input
            value={form.approxCompulsion || ""}
            onChange={(e) => set("approxCompulsion", e.target.value)}
            placeholder="Optional"
            maxLength={80}
          />
        </Field>
        {isMember && form.position && (
          <Field
            label="Reporting managers"
            required
            hint="Select who this member reports to"
            error={errors.reportingManagers}
          >
            <AntMultiSelect
              value={normalizeReportingManagerIds(form.reportingManagers)}
              options={managerOptions}
              onChange={(vals) => set("reportingManagers", vals || [])}
              placeholder="Select reporting manager(s)"
              style={{ width: "100%" }}
              status={errors.reportingManagers ? "error" : undefined}
              getPopupContainer={(node) => node.parentNode}
            />
          </Field>
        )}
        {/* Invitation method (new members only) — manual / email / link, matching legacy Team */}
        {isNew && (
          <Field label="How should they join?" hint="Manual sets a password now; Email/Link lets the member set their own.">
            <div style={{ display: "flex", gap: t.s[2], flexWrap: "wrap" }}>
              {[
                { value: "manual", label: "Set password", icon: <Shield size={14} /> },
                // { value: "email", label: "Email invite", icon: <Mail size={14} /> },
                // { value: "link", label: "Invite link", icon: <Link2 size={14} /> },
              ].map((m) => {
                const active = (form.invitationMethod || "manual") === m.value;
                return (
                  <button key={m.value} type="button" onClick={() => set("invitationMethod", m.value)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6, height: 34, padding: "0 12px", borderRadius: t.r.sm, cursor: "pointer",
                      border: `1px solid ${active ? "transparent" : t.c.border}`, background: active ? t.c.primary : t.c.surface,
                      color: active ? "#fff" : t.c.textSecondary, fontSize: t.font.size.sm, fontWeight: 500, fontFamily: t.font.family,
                    }}>{m.icon}{m.label}</button>
                );
              })}
            </div>
          </Field>
        )}
        {(isNew && (form.invitationMethod || "manual") === "manual" || !isNew) && (
          <Field
            label={isNew ? "Password" : "Password (optional)"}
            required={isNew}
            error={errors.password}
            hint={errors.password ? undefined : (isNew ? "Min 8 characters — the member signs in with this" : "Enter a new password to change it, or leave empty to keep current")}
          >
            <PasswordInput
              invalid={!!errors.password}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={isNew ? "••••••••" : "Leave empty to keep current password"}
            />
          </Field>
        )}
      </DrawerSection>

      {/* Permissions toggles — full set, matching legacy Team */}
      <DrawerSection title="Permissions">
        <PermissionToggle t={t} icon={<EyeOff size={15} />} label="Stealth mode" desc="Hide this member's screen activity" on={form.stealth_mode} onToggle={() => set("stealth_mode", !form.stealth_mode)} />
        <PermissionToggle t={t} icon={<Briefcase size={15} />} label="Add projects" desc="Can create new projects" on={!!form.permissions?.addProject} onToggle={() => setPerm("addProject")} />
        <PermissionToggle t={t} icon={<UserCog size={15} />} label="Add team members" desc="Can invite people" on={!!form.permissions?.addTeamMember} onToggle={() => setPerm("addTeamMember")} />
        <PermissionToggle t={t} icon={<FileText size={15} />} label="View reports" desc="Access analytics & reports" on={form.permissions?.viewReports !== false} onToggle={() => set("permissions", { ...form.permissions, viewReports: !(form.permissions?.viewReports !== false) })} />
        <PermissionToggle t={t} icon={<ActivityPerm size={15} />} label="View activity" desc="See activity timeline & screenshots" on={form.permissions?.viewActivity !== false} onToggle={() => set("permissions", { ...form.permissions, viewActivity: !(form.permissions?.viewActivity !== false) })} />
        <PermissionToggle t={t} icon={<Eye size={15} />} label="Edit profile" desc="Can edit their own profile" on={form.permissions?.editProfile !== false} onToggle={() => set("permissions", { ...form.permissions, editProfile: !(form.permissions?.editProfile !== false) })} />
        {showCrm && <PermissionToggle t={t} icon={<Briefcase size={15} />} label="CRM access" desc="Can access the CRM module" on={!!form.permissions?.crmAccess} onToggle={() => setPerm("crmAccess")} />}
      </DrawerSection>

      {/* Connections (CRM access + Gmail status) — only for existing members AND
          only for CRM-enabled orgs (same whitelist gate as the CRM permission). */}
      {editingUser && showCrm && <ConnectionsSection t={t} user={editingUser} />}
    </div>
  );
}

function PermissionToggle({ t, icon, label, desc, on, onToggle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <span style={{ color: t.c.textTertiary, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.md, color: t.c.text }}>{label}</div>
          {desc && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{desc}</div>}
        </div>
      </div>
      <Toggle on={on} onClick={onToggle} />
    </div>
  );
}

// CRM access + Gmail connect/disconnect for one existing member.
function ConnectionsSection({ t, user }) {
  const b = useBinding();
  const userId = String(user.id || user._id);
  const [crm, setCrm] = React.useState(null);     // { connected, role }
  const [gmail, setGmail] = React.useState(null); // { connected, insights, crm }
  const [crmRole, setCrmRole] = React.useState("agent");
  const [crmLoading, setCrmLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [acc, gm] = await Promise.all([b.getCrmAccess([userId]), b.getGmailStatus([userId])]);
        if (!alive) return;
        const a = acc[userId] || { connected: false, role: "agent" };
        setCrm(a); setCrmRole(a.role || "agent");
        setGmail(gm[userId] || { connected: false });
      } catch (_) { if (alive) { setCrm({ connected: false }); setGmail({ connected: false }); } }
    })();
    return () => { alive = false; };
  }, [ userId]);

  const connectCrm = async () => {
    setCrmLoading(true);
    try { await b.connectCrmAccess(userId, crmRole); setCrm({ connected: true, role: crmRole }); toast.success("CRM access granted"); }
    catch (e) { toast.error(e.response?.data?.message || "Failed to grant access"); }
    finally { setCrmLoading(false); }
  };
  const disconnectCrm = async () => {
    setCrmLoading(true);
    try { await b.disconnectCrmAccess(userId); setCrm({ connected: false }); toast.success("CRM access revoked"); }
    catch (e) { toast.error(e.response?.data?.message || "Failed to revoke access"); }
    finally { setCrmLoading(false); }
  };

  return (
    <DrawerSection title="Connections">
      <ConnectRow
        icon={<Briefcase size={16} />}
        title="CRM access"
        subtitle={crm?.connected ? `Role: ${crmRole === "owner" ? "Owner" : "User"}` : "Allow this member into the CRM"}
        connected={!!crm?.connected}
        loading={crmLoading || crm === null}
        onConnect={connectCrm}
        onDisconnect={disconnectCrm}
        right={!crm?.connected ? (
          // Match CRM exactly: only Owner + User in the UI ("User" maps to
          // backend role 'agent'). Backend still has the 4-role enum.
          <Dropdown
            value={crmRole} onChange={setCrmRole} minWidth={128}
            options={[
              { value: "owner", label: "Owner", sub: "Full admin access" },
              { value: "agent", label: "User", sub: "Standard access" },
            ]}
          />
        ) : null}
      />
      <ConnectRow
        icon={<Mail size={16} />}
        title="Gmail"
        subtitle={gmail?.connected ? [gmail.insights && "Insights", gmail.crm && "CRM"].filter(Boolean).join(" · ") : "Not connected to any TeamGrid Gmail feature"}
        connected={!!gmail?.connected}
        loading={gmail === null}
        disabled
      />
      <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, paddingLeft: 2 }}>
        Gmail connect is done by the member from their own account (separate consent per feature). This shows current status.
      </div>
    </DrawerSection>
  );
}

